import { BarChart3 } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

import { useTranslation } from 'react-i18next';
const reachData = [
    { date: 'Feb 13', impressions: 100000, reach: 90000 },
    { date: 'Feb 20', impressions: 15000, reach: 9000 },
    { date: 'Feb 27', impressions: 12000, reach: 7000 },
    { date: 'Mar 1', impressions: 350000, reach: 170000 },
    { date: 'Mar 3', impressions: 10000, reach: 5000 },
    { date: 'Mar 5', impressions: 8000, reach: 4000 },
    { date: 'Mar 7', impressions: 180000, reach: 90000 },
    { date: 'Mar 9', impressions: 15000, reach: 8000 },
    { date: 'Mar 11', impressions: 10000, reach: 6000 },
    { date: 'Mar 13', impressions: 5000, reach: 2000 }
];

const interactionData = [
    { date: 'Feb 13', likes: 1600, comments: 200 },
    { date: 'Feb 20', likes: 100, comments: 20 },
    { date: 'Feb 27', likes: 80, comments: 10 },
    { date: 'Mar 1', likes: 300, comments: 50 },
    { date: 'Mar 3', likes: 50, comments: 5 },
    { date: 'Mar 5', likes: 1200, comments: 150 },
    { date: 'Mar 7', likes: 100, comments: 15 },
    { date: 'Mar 9', likes: 60, comments: 8 },
    { date: 'Mar 11', likes: 50, comments: 5 },
    { date: 'Mar 13', likes: 30, comments: 2 }
];

const followerData = [
    { date: 'Feb 11', followers: 660 },
    { date: 'Feb 15', followers: 680 },
    { date: 'Feb 20', followers: 690 },
    { date: 'Feb 25', followers: 710 },
    { date: 'Mar 1', followers: 715 },
    { date: 'Mar 5', followers: 730 },
    { date: 'Mar 8', followers: 755 },
    { date: 'Mar 11', followers: 760 },
    { date: 'Mar 13', followers: 770 }
];

export function AnalyticsInsightsCard() {
    const { t } = useTranslation();
    return (
        <div className="col-span-1 md:col-span-2 lg:col-span-4 row-span-1 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 rounded-3xl border border-blue-100 dark:border-blue-900/50 p-6 sm:p-8 shadow-sm overflow-hidden flex flex-col min-h-[400px] relative transition-transform hover:-translate-y-1 hover:shadow-lg duration-300">
            {/* Header / Text area */}
            <div className="relative z-10 mb-8 max-w-xl">
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">{t('landing.mock.analyticsBlurb')}</h3>
            </div>

            {/* Dashboard Mock UI - Performance Trends Grid */}
            <div className="relative z-10 w-full mt-4">

                {/* Section Header */}
                <div className="flex items-center gap-2 mb-4 bg-white/50 dark:bg-gray-800/50 w-fit px-4 py-2 rounded-xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm backdrop-blur-sm">
                    <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    <div>
                        <h4 className="font-bold text-gray-900 dark:text-white text-base leading-tight">{t('landing.mock.performanceTrends')}</h4>
                        <p className="text-[10px] text-gray-500 font-medium tracking-wide">{t('landing.mock.last30Days')}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                    {/* Card 1: IMPRESSIONS VS REACH */}
                    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm flex flex-col transition-shadow hover:shadow-md">
                        <div className="mb-4">
                            <h5 className="text-[10px] font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-0.5">{t('landing.mock.impressionsVsReach')}</h5>
                            <p className="text-[9px] text-gray-400">{t('landing.mock.impressionsVsReachDesc')}</p>
                        </div>

                        {/* Chart Area using Recharts */}
                        <div className="flex-1 w-full mt-4 min-h-[140px] -ml-4 pr-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={reachData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-gray-100 dark:stroke-gray-800" />
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                                        tickFormatter={(value) => value >= 1000 ? `${value / 1000}K` : value}
                                    />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', fontSize: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    />
                                    <Line type="monotone" dataKey="impressions" stroke="#10b981" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                    <Line type="monotone" dataKey="reach" stroke="#8b5cf6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="flex justify-center gap-4 mt-2">
                            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div><span className="text-[8px] font-bold text-emerald-500">{t('landing.mock.impressions')}</span></div>
                            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div><span className="text-[8px] font-bold text-purple-500">{t('landing.mock.reach')}</span></div>
                        </div>
                    </div>

                    {/* Card 2: LIKES & COMMENTS */}
                    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm flex flex-col transition-shadow hover:shadow-md hidden sm:flex">
                        <div className="mb-4">
                            <h5 className="text-[10px] font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-0.5">{t('landing.mock.likesComments')}</h5>
                            <p className="text-[9px] text-gray-400">{t('landing.mock.interactionTrends')}</p>
                        </div>

                        {/* Chart Area using Recharts */}
                        <div className="flex-1 w-full mt-4 min-h-[140px] -ml-4 pr-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={interactionData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-gray-100 dark:stroke-gray-800" />
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                                        tickFormatter={(value) => value >= 1000 ? `${value / 1000}K` : value}
                                    />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', fontSize: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    />
                                    <Line type="monotone" dataKey="likes" stroke="#f43f5e" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                    <Line type="monotone" dataKey="comments" stroke="#60a5fa" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="flex justify-center gap-4 mt-2">
                            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div><span className="text-[8px] font-bold text-rose-500">{t('landing.mock.likes')}</span></div>
                            <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div><span className="text-[8px] font-bold text-blue-400">{t('landing.mock.comments')}</span></div>
                        </div>
                    </div>

                    {/* Card 3: FOLLOWER GROWTH */}
                    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm flex flex-col transition-shadow hover:shadow-md hidden lg:flex">
                        <div className="mb-4 flex justify-between items-start">
                            <div>
                                <h5 className="text-[10px] font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-0.5">{t('landing.mock.followerGrowth')}</h5>
                                <p className="text-[9px] text-gray-400">770 total followers</p>
                            </div>
                            <span className="text-[9px] font-bold text-emerald-600 bg-emerald-100/50 dark:text-emerald-400 dark:bg-emerald-900/30 px-2.5 py-1 rounded">
                                ↗ +108 <span className="opacity-70 font-medium ml-0.5">(16.3%)</span>
                            </span>
                        </div>

                        {/* Chart Area using Recharts */}
                        <div className="flex-1 w-full mt-4 min-h-[140px] -ml-2 pr-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={followerData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorFollowers" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-gray-100 dark:stroke-gray-800" />
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        domain={['dataMin - 10', 'dataMax + 10']}
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                                    />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', fontSize: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    />
                                    <Area type="monotone" dataKey="followers" stroke="#2563eb" strokeWidth={2} fillOpacity={1} fill="url(#colorFollowers)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="h-[14px] mt-2"></div> {/* Spacer to align with other cards which have legends */}
                    </div>
                </div>

            </div>

            {/* Background decorative blob */}
            <div className="absolute bottom-[-20%] left-[30%] w-[30%] h-[50%] bg-blue-400/20 dark:bg-blue-600/20 rounded-full blur-[80px] pointer-events-none z-0"></div>
        </div>
    );
}
